import { useMemo } from "react";

export const useRiskAnalysis = ({
  currentStrikeData,
  expectedDays,
  expectedMove,
}) => {
  const riskAnalysisData = useMemo(() => {
    if (!currentStrikeData)
      return { scenarios: [], breakEven: null, timeDecay: [] };

    const tradingDay =
      currentStrikeData.trading_days.find(
        (td) => td.trading_days_passed === expectedDays,
      ) || currentStrikeData.trading_days[0];

    const scenarios = tradingDay.scenarios;
    const entryPrice = tradingDay.theoretical_price_now;
    const currentStockPrice = tradingDay.current_stock_price;

    const allScenarios = Object.entries(scenarios).map(
      ([movePercent, scenario]) => {
        const move = parseFloat(movePercent.replace("%", ""));
        const isProfit = scenario.price_change > 0;
        const isBreakEven = Math.abs(scenario.price_change) < 0.01;
        const lossAmount = isProfit ? 0 : Math.abs(scenario.price_change);
        const lossPercent = isProfit ? 0 : Math.abs(scenario.price_change_pct);

        return {
          movePercent,
          move,
          futureStockPrice: scenario.future_stock_price,
          exitPrice: scenario.theoretical_price_future,
          priceChange: scenario.price_change,
          returnPct: scenario.price_change_pct,
          entryPrice,
          isProfit,
          isBreakEven,
          lossAmount,
          lossPercent,
          riskLevel:
            scenario.price_change_pct < -50
              ? "critical"
              : scenario.price_change_pct < -25
                ? "high"
                : scenario.price_change_pct < 0
                  ? "medium"
                  : "safe",
        };
      },
    );

    allScenarios.sort((a, b) => a.move - b.move);

    const breakEvenScenario =
      allScenarios.find((s) => s.isBreakEven) ||
      allScenarios.find((s) => s.priceChange >= 0);

    const breakEvenStockPrice =
      breakEvenScenario?.futureStockPrice || currentStockPrice;
    const breakEvenMove = breakEvenScenario
      ? (
          ((breakEvenStockPrice - currentStockPrice) / currentStockPrice) *
          100
        ).toFixed(2)
      : 0;

    const timeDecayData = currentStrikeData.trading_days.map((td) => {
      const scenario = td.scenarios[`${expectedMove}%`];
      return {
        day: td.trading_days_passed,
        daysToExpiration: td.days_to_expiration,
        exitPrice: scenario?.theoretical_price_future || 0,
        priceChange: scenario?.price_change || 0,
        returnPct: scenario?.price_change_pct || 0,
        entryPrice: td.theoretical_price_now,
      };
    });

    return {
      scenarios: allScenarios,
      breakEven: {
        stockPrice: breakEvenStockPrice,
        move: breakEvenMove,
        scenario: breakEvenScenario,
      },
      timeDecay: timeDecayData,
      entryPrice,
      currentStockPrice,
    };
  }, [currentStrikeData, expectedDays, expectedMove]);

  const profitLossChartData = useMemo(() => {
    return riskAnalysisData.scenarios.map((s) => ({
      move: s.movePercent,
      moveNum: s.move,
      "Profit/Loss": s.priceChange,
      "Return %": s.returnPct,
    }));
  }, [riskAnalysisData]);

  return { riskAnalysisData, profitLossChartData };
};
